import { AccountingSystem } from '../accounting/accounting.enums';
import { Entity, AuditDetails } from '../entity';

export enum PaymentType {

    // ACCRECPAYMENT	Accounts Receivable Payment
    // ACCPAYPAYMENT	Accounts Payable Payment
    Receivable = 'Accounts Receivable',
    Payable = 'Accounts Payable',


    // ARCREDITPAYMENT	Accounts Receivable Credit Payment (Refund)
    // APCREDITPAYMENT	Accounts Payable Credit Payment(Refund)
    ReceivableCredit = 'Accounts Receivable Credit',
    PayableCredit = 'Accounts Payable Credit',


    // Allocation of a credit note against another invoice/bill (no cash movement).
    // Only produced by the Acumatica sync; rolled into the credit note's `creditAmount`
    // (Credit Allocated) rather than `amountRefunded`. See WBS-8390.
    ReceivableCreditAllocation = 'Accounts Receivable Credit Allocation',
    PayableCreditAllocation = 'Accounts Payable Credit Allocation',


    // ARPREPAYMENTPAYMENT	Accounts Receivable Prepayment Payment(Refund)
    // APPREPAYMENTPAYMENT	Accounts Payable Prepayment Payment(Refund)
    ReceivablePrepayment = 'Accounts Receivable Prepayment',
    PayablePrepayment = 'Accounts Payable Prepayment',


    // AROVERPAYMENTPAYMENT	Accounts Receivable Overpayment Payment(Refund)
    // APOVERPAYMENTPAYMENT	Accounts Payable Overpayment Payment(Refund)
    ReceivableOverpayment = 'Accounts Receivable Overpayment',
    PayableOverpayment = 'Accounts Payable Overpayment',
}

/**
 * Money coming IN — a customer settling one of our invoices — and its refund / credit / prepayment
 * variants. The `entity` on these payments points at an Invoice.
 */
export const RECEIVABLE_PAYMENT_TYPES = [PaymentType.Receivable, PaymentType.ReceivableCredit, PaymentType.ReceivableCreditAllocation, PaymentType.ReceivablePrepayment, PaymentType.ReceivableOverpayment];

/**
 * Money going OUT — us settling a supplier's bill — and its refund / credit / prepayment variants.
 * The `entity` on these payments points at a Bill.
 */
export const PAYABLE_PAYMENT_TYPES = [PaymentType.Payable, PaymentType.PayableCredit, PaymentType.PayableCreditAllocation, PaymentType.PayablePrepayment, PaymentType.PayableOverpayment];

/** Which side of the ledger a payment sits on, and therefore which document its `entity` points at. */
export function isPayablePaymentType(type: PaymentType | undefined): boolean {

    return PAYABLE_PAYMENT_TYPES.includes(type);
}

export enum PaymentStatus {
    Pending = 'Pending',
    Success = 'Success',
    Error = 'Error',
    IntegrationError = 'Integration Error',
    WorkBuddyError = 'WorkBuddy Error'
}

export enum PaymentMethodType {
    Manual = 'Manual',
    Online = 'Online'
}

export enum ReconciliationStatus {
    Reconciled = 'Reconciled',
    NotReconciled = 'Not Reconciled',
}

export enum PaymentSource {
    Xero = 'Xero',
    WorkBuddy = 'WorkBuddy',
    Stripe = 'Stripe',
    QuickBooks = 'QuickBooks',
    MYOB = 'MYOB',
    WBBulkImport = 'WB Bulk Import',
    Jobpac = 'Jobpac',
    Acumatica = 'Acumatica'
}

export interface PaymentMethodEntity extends Entity {
    type: PaymentMethodType;
    requiresReconcile: boolean;
}

export interface PaymentMethod extends PaymentMethodEntity {
    availableOnFieldApp: boolean;
    isServiceConnection: boolean;
    companies: Entity[];
    inUse?: boolean;
}

export interface Payment extends Entity, AuditDetails {
    entity: Entity;
    amount: number;
    date: Date;
    note?: string;
    reference: string;
    method: PaymentMethodEntity;
    account?: Entity;
    type: PaymentType;
    status: PaymentStatus;
    merchant?: any;
    receiver?: Entity;
    company?: Entity;
    payer?: string;
    timezone?: string;
    reconciled?: ReconciliationStatus;
    integrationId?: string;
    source?: PaymentSource;
    accountingSystem?: AccountingSystem;
}


export interface PaymentIntentResult {
    success: boolean;
    intent: any;
    message: string;
}


export interface PaymentEditorInfo {
    amount: number;
    amountEditable: boolean,
    type: PaymentType,
    title?: 'Receive' | 'Refund' | 'Pay',
}

export interface TerminalTokenResult {
    success: boolean;
    token: string;
    message: string;
}

export interface TerminalLocationResult {
    success: boolean;
    locationId: string;
    message: string;
}